import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DIRECTUS_PROJECTS_ENDPOINT } from '../../api';
import usePaginationWithURL from '../../hooks/usePaginationWithURL';
import { DeleteConfirmationModal } from '../../ui/atoms';
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import Pagination from '../../ui/molecules/Pagination';
import TableActions from '../../ui/molecules/TableActions';
import TablePageLayout from '../../ui/molecules/TablePageLayout';
import createProjectIcon from '../../img/create-project.svg';
import StandardTable from '../../ui/molecules/StandardTable';
import { formatDisplayDateTime } from '../../utils/dateFormatting';

const EmptyState = ({ onPrimaryClick }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center h-full text-center py-24">
      <img 
        src={createProjectIcon} 
        alt={t('projects.createNew')} 
        className="mx-auto h-32 w-32 text-primary-400"
      />
      <h1 className="text-3xl font-medium text-gray-900 mt-6">{t('projects.emptyState.title')}</h1>
      <p className="mt-1 text-base text-gray-800 opacity-80">{t('projects.emptyState.description')}</p>
      <div className="mt-6">
        <button
          type="button"
          onClick={onPrimaryClick}
          className="btn btn-md btn-rounded btn-primary"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          {t('projects.emptyState.button')}
        </button>
      </div>
    </div>
  );
};

const ProjectSkeleton = () => (
  <tr className="animate-pulse">
    <td className="px-0 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-3/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
  </tr>
);

function ProjectsTable() {
  const { t, i18n } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('directus_token');
  const userId = localStorage.getItem('user_id');

  // Use standardized pagination hook
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalCount,
    setTotalCount,
    handlePageChange,
    handleItemsPerPageChange
  } = usePaginationWithURL({
    defaultItemsPerPage: 25,
    allowedItemsPerPage: [10, 25, 50, 100],
    scrollToTop: true,
    scrollOffset: 0
  });

  const primaryClick = useCallback(() => navigate('/projects/create-project'), [navigate]);

  // Checkbox functions
  const handleSelectProject = useCallback((projectId) => {
    setSelectedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedProjects.size === projects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(projects.map(project => project.id)));
    }
  }, [projects, selectedProjects.size]);

  // Show delete modal
  const handleDeleteClick = useCallback(() => {
    if (selectedProjects.size === 0) return;
    setShowDeleteModal(true);
  }, [selectedProjects.size]);

  // Bulk delete function
  const handleBulkDelete = useCallback(async () => {
    setShowDeleteModal(false);
    setIsDeleting(true);
    
    try {
      const deletePromises = Array.from(selectedProjects).map(async (projectId) => {
        const baseURL = window.location.hostname === 'localhost' ? '/directus' : 'https://panel.prosecaptain.com';
        const response = await fetch(`${baseURL}/items/projects/${projectId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to delete project ${projectId}`);
        }
        return projectId;
      });

      await Promise.all(deletePromises);
      
      setProjects(prev => prev.filter(project => !selectedProjects.has(project.id)));
      setSelectedProjects(new Set());
      
    } catch (error) {
      console.error('Error deleting projects:', error);
      alert('Error deleting some projects. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedProjects, token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(DIRECTUS_PROJECTS_ENDPOINT, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: { owner: userId },
            fields: ['*', 'user_created.first_name', 'user_created.last_name', 'owner_details.first_name', 'owner_details.last_name'],
            limit: itemsPerPage,
            page: currentPage
          }
        });
        
        const processedProjects = response.data.data
          .map(project => ({
            ...project,
            owner: project.user_created?.first_name 
              ? `${project.user_created.first_name} ${project.user_created.last_name}`
              : project.owner_details?.first_name 
                ? `${project.owner_details.first_name} ${project.owner_details.last_name}`
                : t('common.unknown')
          }))
          .sort((a, b) => new Date(b.date_created) - new Date(a.date_created));

        setProjects(processedProjects);
        setTotalCount(response.data.meta.total_count || processedProjects.length);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, token, currentPage, itemsPerPage, t, setTotalCount]);

  const formatDate = useCallback((dateString) => {
    return formatDisplayDateTime(dateString, i18n.language);
  }, [i18n.language]);

  const handleRowClick = useCallback((project) => {
    navigate(`/projects/${project.id}`);
  }, [navigate]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'archived':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = useMemo(() => [
    {
      key: 'project_name',
      title: t('projects.projectName'),
      render: (project) => (
        <div className="font-medium text-gray-900">{project.project_name}</div>
      ),
      headerClassName: 'py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0',
      cellClassName: 'whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0'
    },
    {
      key: 'status',
      title: t('common.status'),
      render: (project) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
          {t(`projects.status.${project.status || 'draft'}`)}
        </span>
      ),
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    },
    {
      key: 'owner',
      title: t('common.owner'),
      render: (project) => project.owner,
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    },
    {
      key: 'date_created',
      title: t('common.creationDate'),
      render: (project) => formatDate(project.date_created),
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    }
  ], [t, formatDate]);


  return (
    <TablePageLayout
      title={t('projects.title')}
      description={t('projects.description')}
      primaryAction={t('projects.createNew')}
      onPrimaryAction={primaryClick}
    >
      <div className="mb-4">
        <TableActions
          actions={[
            {
              label: isDeleting ? t('common.deleting') : t('common.delete'),
              icon: TrashIcon,
              onClick: handleDeleteClick,
              disabled: selectedProjects.size === 0 || isDeleting,
              loading: isDeleting,
              variant: 'outline',
            },
          ]}
        />
      </div>

      <StandardTable
        columns={columns}
        data={projects}
        loading={loading}
        SkeletonRow={ProjectSkeleton}
        EmptyState={EmptyState}
        onRowClick={handleRowClick}
        selectable={true}
        selectedItems={selectedProjects}
        onSelectItem={handleSelectProject}
        onSelectAll={handleSelectAll}
        firstColumnTitle={t('projects.projectName')}
      />

      {!loading && projects.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalCount={totalCount}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
      
      <DeleteConfirmationModal
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        selectedCount={selectedProjects.size}
        isDeleting={isDeleting}
      />
    </TablePageLayout>
  );
}

export default ProjectsTable;